/* Hairy Pony Routine Builder for Shopify Dawn.
   Uses the same pre-order line-item property names as the Dawn buy buttons.
   No Horizon hp-preorder.js or hp-preorder-data dependencies. */
(() => {
  if (customElements.get('hp-routine-builder')) return;

  class HPRoutineBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.cards = [...this.querySelectorAll('[data-routine-card]')];
      this.addButton = this.querySelector('[data-routine-add]');
      this.buttonLabel = this.querySelector('[data-routine-button-label]');
      this.totalLabel = this.querySelector('[data-routine-total]');
      this.preorderNote = this.querySelector('[data-routine-preorder-note]');
      this.errorMessage = this.querySelector('[data-routine-error]');
      this.singleProduct = this.dataset.count === '1';
      this.busy = false;

      this.cards.forEach((card) => {
        const checkbox = card.querySelector('[data-routine-check]');
        if (checkbox) checkbox.addEventListener('change', () => this.update());

        const variantInput = card.querySelector('[data-routine-variant]');
        if (variantInput && variantInput.tagName === 'SELECT') {
          variantInput.addEventListener('change', () => {
            const price = card.querySelector('[data-routine-price]');
            const chosen = variantInput.selectedOptions[0];
            if (price && chosen) price.textContent = this.formatMoney(Number(chosen.dataset.price));
            this.update();
          });
        }
      });

      this.addButton.addEventListener('click', () => this.addSelected());
      this.update();
    }

    selectedCards() {
      if (this.singleProduct) return this.cards;
      return this.cards.filter((card) => card.querySelector('[data-routine-check]')?.checked);
    }

    chosenVariant(card) {
      const input = card.querySelector('[data-routine-variant]');
      return input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || 'en-AU', {
          style: 'currency',
          currency: this.dataset.currency || 'AUD'
        }).format(cents / 100);
      } catch (_) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    update() {
      const selected = this.selectedCards();
      const hasSelection = selected.length > 0;
      const includesPreorder = selected.some((card) => card.dataset.preorder === 'true');
      const cents = selected.reduce((sum, card) => {
        return sum + Number(this.chosenVariant(card)?.dataset.price || 0);
      }, 0);

      this.addButton.dataset.hasSelection = String(hasSelection);
      this.addButton.disabled = this.busy || !hasSelection;
      this.buttonLabel.textContent = this.singleProduct
        ? (includesPreorder ? 'PRE-ORDER' : 'ADD')
        : (hasSelection ? 'ADD' : 'SELECT ITEMS');
      this.totalLabel.textContent = hasSelection ? ' - ' + this.formatMoney(cents) : '';
      this.preorderNote.hidden = !includesPreorder;
    }

    async addSelected() {
      if (this.busy) return;
      const selected = this.selectedCards();
      if (!selected.length) return;

      const items = selected.map((card) => {
        const item = {
          id: Number(card.querySelector('[data-routine-variant]').value),
          quantity: 1
        };

        // IMPORTANT: Same private Shopify cart properties as Dawn's buy buttons.
        if (card.dataset.preorder === 'true') {
          item.properties = { '_Pre-order status': 'Available to pre-order' };
          if (card.dataset.dispatch) {
            item.properties['_Estimated dispatch'] = card.dataset.dispatch;
          }
        }
        return item;
      });

      this.busy = true;
      this.addButton.disabled = true;
      this.buttonLabel.textContent = 'ADDING...';
      this.totalLabel.textContent = '';
      this.errorMessage.hidden = true;

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Unable to add the selected products.');
        }
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.errorMessage.textContent = error.message || 'Something went wrong. Please try again.';
        this.errorMessage.hidden = false;
        this.busy = false;
        this.update();
      }
    }
  }

  customElements.define('hp-routine-builder', HPRoutineBuilder);
})();
